import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, Loader2, Save } from "lucide-react";
import { api, extractErr, usePortalAuth } from "@/portal/PortalAuth";
import { btn, ghost, card, inp } from "@/portal/PortalShell";

const VARS = "{{guest_name}} {{guest_email}} {{property_name}} {{property_address}} {{room_name}} {{check_in}} {{check_out}} {{nights}} {{monthly_rate}} {{payment_schedule}} {{total}} {{amount_due_today}} {{security_deposit}} {{cleaning_fee}} {{management_fee_pct}} {{house_rules}}";

export default function AgreementTemplates() {
  const [list, setList] = useState([]);
  const load = async () => {
    try { const { data } = await api.get("/admin/booking/agreement-templates"); setList(Array.isArray(data) ? data : []); }
    catch (e) { toast.error(extractErr(e)); }
  };
  useEffect(() => { load(); }, []);
  return (
    <div className="space-y-4" data-testid="agreement-templates">
      <div className={card}>
        <p className="text-xs uppercase tracking-[0.2em] font-medium text-[#8A9A86]">Monthly stay agreements</p>
        <p className="mt-1 text-sm text-[#78716C]">Guests can only sign an approved template. Replace every [PLACEHOLDER] with attorney-reviewed wording before approving. Editing creates a new version that needs approval again; signed agreements keep the exact text and timestamp.</p>
        <p className="mt-2 text-[11px] text-[#78716C] break-words">Available variables: {VARS}</p>
      </div>
      {list.map((t) => <TemplateEditor key={t.key} t={t} onSaved={load} />)}
    </div>
  );
}

function TemplateEditor({ t, onSaved }) {
  const { user } = usePortalAuth();
  const [title, setTitle] = useState(t.title);
  const [body, setBody] = useState(t.body);
  const [busy, setBusy] = useState("");
  const dirty = title !== t.title || body !== t.body;
  const placeholders = (body.match(/\[PLACEHOLDER/g) || []).length;
  const canApprove = ["admin", "manager"].includes(user?.role);
  const act = async (key, fn, ok) => {
    setBusy(key);
    try { await fn(); toast.success(ok); onSaved(); } catch (e) { toast.error(extractErr(e)); } finally { setBusy(""); }
  };
  const approve = () => {
    if (placeholders && !window.confirm(`This template still has ${placeholders} [PLACEHOLDER] marker(s). Approve anyway?`)) return;
    act("approve", () => api.post(`/admin/booking/agreement-templates/${t.key}/approve`), "Template approved for guest use");
  };
  return (
    <div className={card} data-testid={`template-${t.key}`}>
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <p className="text-sm text-[#1C1917] font-medium">{t.title} <span className="text-xs text-[#78716C] font-normal">· v{t.version}</span></p>
        <span data-testid={`template-status-${t.key}`} className={`text-[11px] rounded-full px-2.5 py-0.5 ${t.status === "approved" ? "bg-[#E8EFE7] text-[#4A6A48]" : "bg-[#FEF3C7] text-[#92400E]"}`}>
          {t.status === "approved" ? `Approved by ${t.approved_by}` : "Draft · not available to guests"}
        </span>
      </div>
      <input value={title} onChange={(e) => setTitle(e.target.value)} data-testid={`template-title-${t.key}`} className={inp + " mb-2"} />
      <textarea rows={14} value={body} onChange={(e) => setBody(e.target.value)} data-testid={`template-body-${t.key}`}
        className="w-full rounded-lg bg-white border border-[#E7E5E4] focus:outline-none focus:ring-1 focus:ring-[#8A9A86] px-3 py-2 text-[#1C1917] text-xs font-mono leading-relaxed" />
      <p className="mt-1 text-xs text-[#78716C]">{placeholders} placeholder{placeholders === 1 ? "" : "s"} remaining.</p>
      <div className="mt-3 flex flex-wrap gap-2">
        <button disabled={!dirty || !!busy} data-testid={`template-save-${t.key}`} className={ghost + " text-xs"}
          onClick={() => act("save", () => api.put(`/admin/booking/agreement-templates/${t.key}`, { title, body }), "Saved as new draft version")}>
          {busy === "save" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Save draft
        </button>
        {canApprove && t.status !== "approved" && (
          <button disabled={dirty || !!busy} data-testid={`template-approve-${t.key}`} className={btn + " text-xs"} onClick={approve}>
            <CheckCircle2 className="w-3.5 h-3.5" /> Approve for guest use
          </button>
        )}
      </div>
    </div>
  );
}
